export const MY_TOURNAMENTS_PAGE_SIZE = 10;

export const MY_TOURNAMENTS_DIRECTORY_COLUMN_DEFS = [
  { id: "tournament_id", label: "Tournament ID", defaultVisible: true },
  { id: "tournament_name", label: "Tournament Name", defaultVisible: true },
  { id: "sport_type", label: "Sport", defaultVisible: true },
  { id: "tournament_level", label: "Level", defaultVisible: true },
  { id: "tournament_date", label: "Date", defaultVisible: true },
  { id: "location", label: "Location", defaultVisible: true },
  { id: "status", label: "Status", defaultVisible: true },
  { id: "result", label: "Result", defaultVisible: true },
  { id: "payment_amount", label: "Amount", defaultVisible: true },
  { id: "payment_status", label: "Payment", defaultVisible: true },
];

export const MY_TOURNAMENTS_TIMELINE_COLUMN_DEFS = [
  { id: "tournament", label: "Tournament", defaultVisible: true },
  { id: "year", label: "Year", defaultVisible: true },
  { id: "level", label: "Level", defaultVisible: true },
  { id: "status", label: "Status", defaultVisible: true },
  { id: "result", label: "Result", defaultVisible: true },
];

export const MY_TOURNAMENTS_DIRECTORY_VISIBILITY_KEY =
  "girinestham.myTournaments.directory.columnVisibility";

export const MY_TOURNAMENTS_TIMELINE_VISIBILITY_KEY =
  "girinestham.myTournaments.timeline.columnVisibility";

export function defaultMyTournamentsFilters() {
  return { search: "", column: "", value: "" };
}

export function formatTournamentDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return "—";
  }
}

export function formatTournamentAmount(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `₹${n.toFixed(2)}`;
  }
}

export function tournamentStatusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "upcoming") return "mm-status-badge mm-status-badge--pending";
  if (s === "ongoing") return "mm-status-badge mm-status-badge--active";
  if (s === "completed") return "mm-status-badge mm-status-badge--muted";
  return "mm-status-badge mm-status-badge--muted";
}

export function resultBadgeClass(result) {
  const s = String(result || "").toLowerCase();
  if (s === "pending" || s === "—") return "mm-status-badge mm-status-badge--pending";
  if (s.includes("winner") || s.includes("1st")) {
    return "mm-status-badge mm-status-badge--active";
  }
  return "mm-status-badge mm-status-badge--muted";
}

export function payChipClass(status) {
  return `mm-pay-chip mm-pay-chip--${String(status || "none")
    .toLowerCase()
    .replace(/\s+/g, "-")}`;
}

export function paginateList(items, page, pageSize = MY_TOURNAMENTS_PAGE_SIZE) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    page: safePage,
    totalPages,
    total,
    slice: items.slice(start, start + pageSize),
  };
}

export function directoryCellValue(colId, row) {
  switch (colId) {
    case "tournament_date":
      return formatTournamentDate(row.tournament_date);
    case "payment_amount":
      return formatTournamentAmount(row.payment_amount ?? row.participation_fee);
    case "payment_status": {
      if (row.payment_status && row.payment_status !== "No payment") {
        return row.payment_status;
      }
      if (row.payment_id != null || row.payment_amount != null) {
        return "Paid";
      }
      return "No payment";
    }
    case "payment_date":
      return formatTournamentDate(row.payment_date);
    default:
      return row?.[colId] ?? "—";
  }
}

export function timelineCellValue(colId, row) {
  switch (colId) {
    case "tournament":
      return row.tournament || row.tournament_name || "—";
    case "tournament_date":
    case "payment_date":
      return formatTournamentDate(row[colId]);
    case "payment_amount":
      return formatTournamentAmount(row.payment_amount ?? row.participation_fee);
    case "payment_status":
      return directoryCellValue("payment_status", row);
    default:
      return row?.[colId] ?? "—";
  }
}

export function directorySearchableText(row) {
  return [
    row.tournament_id,
    row.tournament_name,
    row.sport_type,
    row.tournament_level,
    row.location,
    row.status,
    row.result,
    row.participation_status,
    row.payment_status,
    row.receipt_no,
    row.transaction_id,
    row.payment_mode,
  ]
    .filter(Boolean)
    .join(" ");
}

export function filterDirectoryRows(rows, filters, cellValueFn = directoryCellValue) {
  let result = Array.isArray(rows) ? [...rows] : [];
  const search = String(filters?.search ?? "")
    .trim()
    .toLowerCase();
  if (search) {
    result = result.filter((row) =>
      directorySearchableText(row).toLowerCase().includes(search)
    );
  }
  const col = String(filters?.column ?? "").trim();
  const val = String(filters?.value ?? "").trim();
  if (col && val) {
    result = result.filter((row) => {
      const cell = cellValueFn(col, row);
      return String(cell ?? "").trim().toLowerCase() === val.toLowerCase();
    });
  }
  return result;
}

export function buildValueOptionsFromRows(rows, columnId, cellValueFn = directoryCellValue) {
  const seen = new Set();
  const opts = [];
  for (const row of rows || []) {
    const raw = cellValueFn(columnId, row);
    if (raw == null || raw === "" || raw === "—") continue;
    const label = String(raw);
    if (!seen.has(label)) {
      seen.add(label);
      opts.push({ value: label, label });
    }
  }
  return opts.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" })
  );
}

export function loadColumnVisibility(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function createInitialColumnVisibility(storageKey, columnDefs) {
  const saved = loadColumnVisibility(storageKey);
  const initial = {};
  for (const c of columnDefs) {
    initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  }
  return initial;
}
